import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, CheckCircle, BookOpen, FileText, Loader2, AlertCircle, Globe, Twitter, Instagram } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublisherPage = () => {
  const { slug } = useParams();
  const [publisher, setPublisher] = useState(null);
  const [series, setSeries] = useState([]);
  const [novels, setNovels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('comics');

  useEffect(() => {
    fetchPublisher();
  }, [slug]);

  const fetchPublisher = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch publisher info
      const pubRes = await fetch(`${API_URL}/api/publishers/${slug}`);
      if (!pubRes.ok) {
        if (pubRes.status === 404) {
          throw new Error('Publisher not found');
        }
        throw new Error('Failed to load publisher');
      }
      const pubData = await pubRes.json();
      setPublisher(pubData);

      // Fetch series and novels in parallel
      const [seriesRes, novelsRes] = await Promise.all([
        fetch(`${API_URL}/api/publishers/${slug}/series?limit=50`),
        fetch(`${API_URL}/api/publishers/${slug}/novels?limit=50`)
      ]);

      if (seriesRes.ok) {
        const seriesData = await seriesRes.json();
        setSeries(seriesData.series || []);
      }
      if (novelsRes.ok) {
        const novelsData = await novelsRes.json();
        setNovels(novelsData.novels || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSocialIcon = (platform) => {
    const icons = {
      twitter: Twitter,
      instagram: Instagram,
      website: Globe,
    };
    return icons[platform.toLowerCase()] || ExternalLink;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center" data-testid="publisher-loading">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center gap-4" data-testid="publisher-error">
        <AlertCircle className="w-12 h-12 text-red-500" />
        <p className="text-gray-400">{error}</p>
        <Link to="/comics" className="text-purple-400 hover:text-purple-300">
          Browse Comics →
        </Link>
      </div>
    );
  }

  if (!publisher) return null;

  return (
    <div className="min-h-screen bg-gray-950" data-testid="publisher-page">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-purple-900/20 to-gray-950 border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row items-start gap-8">
            {/* Publisher Logo */}
            <div className="w-32 h-32 md:w-40 md:h-40 bg-gray-800 rounded-2xl flex items-center justify-center overflow-hidden flex-shrink-0">
              {publisher.logo_url ? (
                <img 
                  src={publisher.logo_url} 
                  alt={publisher.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-4xl font-bold text-gray-600">
                  {publisher.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>

            {/* Publisher Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl md:text-4xl font-bold text-white" data-testid="publisher-name">
                  {publisher.name}
                </h1>
                {publisher.verified && (
                  <CheckCircle className="w-6 h-6 text-purple-500" title="Verified Publisher" />
                )}
              </div>

              {publisher.description && (
                <p className="text-gray-400 text-lg mb-4 max-w-2xl">
                  {publisher.description}
                </p>
              )}

              {/* Stats */}
              <div className="flex flex-wrap gap-6 mb-6">
                <div className="flex items-center gap-2 text-gray-300">
                  <BookOpen className="w-5 h-5 text-purple-500" />
                  <span className="font-semibold">{publisher.series_count || 0}</span>
                  <span className="text-gray-500">Comics</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <FileText className="w-5 h-5 text-purple-500" />
                  <span className="font-semibold">{publisher.novel_count || 0}</span>
                  <span className="text-gray-500">Novels</span>
                </div>
              </div>

              {/* Links */}
              <div className="flex flex-wrap gap-3">
                {publisher.website_url && (
                  <a
                    href={publisher.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-300 transition-colors"
                  >
                    <Globe className="w-4 h-4" />
                    Website
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {publisher.social_links && Object.entries(publisher.social_links).map(([platform, url]) => {
                  const Icon = getSocialIcon(platform);
                  return (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-300 transition-colors capitalize"
                    >
                      <Icon className="w-4 h-4" />
                      {platform}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Tabs */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 border-b border-gray-800">
          <button
            onClick={() => setActiveTab('comics')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              activeTab === 'comics'
                ? 'text-purple-400 border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-gray-300'
            }`}
            data-testid="tab-comics"
          >
            Comics ({series.length})
          </button>
          <button
            onClick={() => setActiveTab('novels')}
            className={`px-6 py-3 font-medium transition-colors relative ${
              activeTab === 'novels'
                ? 'text-purple-400 border-b-2 border-purple-500'
                : 'text-gray-400 hover:text-gray-300'
            }`}
            data-testid="tab-novels"
          >
            Novels ({novels.length})
          </button>
        </div>

        {/* Content Grid */}
        {activeTab === 'comics' && (
          <div data-testid="comics-grid">
            {series.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No comics published yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {series.map((s) => (
                  <Link
                    key={s.id}
                    to={`/series/${s.id}`}
                    className="group"
                    data-testid={`series-card-${s.id}`}
                  >
                    <div className="aspect-[3/4] bg-gray-800 rounded-lg overflow-hidden mb-2 relative">
                      {s.cover_url || s.cover_thumb_url ? (
                        <img
                          src={s.cover_thumb_url || s.cover_url}
                          alt={s.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-600">
                          <BookOpen className="w-12 h-12" />
                        </div>
                      )}
                    </div>
                    <h3 className="font-medium text-gray-200 group-hover:text-purple-400 transition-colors line-clamp-2">
                      {s.title}
                    </h3>
                    {s.creator_name && (
                      <p className="text-sm text-gray-500">{s.creator_name}</p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'novels' && (
          <div data-testid="novels-grid">
            {novels.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No novels published yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {novels.map((n) => (
                  <Link
                    key={n.id}
                    to={`/novels/${n.id}`}
                    className="group"
                    data-testid={`novel-card-${n.id}`}
                  >
                    <div className="aspect-[3/4] bg-gray-800 rounded-lg overflow-hidden mb-2 relative">
                      {n.cover_url ? (
                        <img
                          src={n.cover_url}
                          alt={n.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-600">
                          <FileText className="w-12 h-12" />
                        </div>
                      )}
                    </div>
                    <h3 className="font-medium text-gray-200 group-hover:text-purple-400 transition-colors line-clamp-2">
                      {n.title}
                    </h3>
                    {n.creator_name && (
                      <p className="text-sm text-gray-500">{n.creator_name}</p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublisherPage;
